import React, { useCallback, useRef } from 'react';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Trash2 } from '@lifeomic/chromicons';
import { defineMessages, useIntl } from 'react-intl';
import { LexicalEditor } from 'lexical';
import { Annotation, HIGHLIGHT_COLORS } from '../../TextAnnotation/types';
import { removeAnnotationMarks } from './AITools/ImpressionEditor/MarkdownEditor/plugins/AnnotationPlugin';

const intlMessages = defineMessages({
  sectionTitle: {
    id: 'annotationsList.sectionTitle',
    defaultMessage: 'Annotations',
  },
  emptyState: {
    id: 'annotationsList.emptyState',
    defaultMessage: 'No requested edits yet.',
  },
  commentPlaceholder: {
    id: 'annotationsList.commentPlaceholder',
    defaultMessage: 'Add a comment...',
  },
  deleteAnnotation: {
    id: 'annotationsList.deleteAnnotation',
    defaultMessage: 'Delete annotation',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  card: {
    borderRadius: theme.pxToRem(6),
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1, 0, 1.5),
  },
  highlight: {
    flex: 1,
    minWidth: 0,
    fontSize: theme.pxToRem(13),
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.pxToRem(3),
  },
  deleteButton: {
    flexShrink: 0,
    padding: theme.spacing(0.5),
    minWidth: 'unset',
  },
  commentArea: {
    padding: theme.spacing(1, 1.5, 1.5),
  },
}));

const AnnotationCard: React.FC<{
  annotation: Annotation;
  onDelete: (id: string) => void;
  onCommentChange: (id: string, comment: string) => void;
}> = React.memo(({ annotation, onDelete, onCommentChange }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const color =
    HIGHLIGHT_COLORS[annotation.colorIndex % HIGHLIGHT_COLORS.length];

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onCommentChange(annotation.id, e.target.value);
    },
    [annotation.id, onCommentChange],
  );

  const handleDeleteClick = useCallback(() => {
    onDelete(annotation.id);
  }, [annotation.id, onDelete]);

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <div
          className={classes.highlight}
          style={{
            backgroundColor: color.bg,
            borderLeft: `3px solid ${color.border}`,
          }}
        >
          &ldquo;{annotation.selectedText}&rdquo;
        </div>
        <IconButton
          className={classes.deleteButton}
          aria-label={formatMessage(intlMessages.deleteAnnotation)}
          icon={Trash2}
          size={0}
          color="default"
          onClick={handleDeleteClick}
        />
      </div>
      <div className={classes.commentArea}>
        <TextArea
          aria-label="Comment"
          placeholder={formatMessage(intlMessages.commentPlaceholder)}
          value={annotation.comment}
          onChange={handleChange}
          fullWidth
          rows={2}
        />
      </div>
    </div>
  );
});

type AnnotationsListProps = {
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  editorRef: React.RefObject<LexicalEditor | null>;
};

export const AnnotationsList: React.FC<AnnotationsListProps> = ({
  annotations,
  onAnnotationsChange,
  editorRef,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  const handleDelete = useCallback(
    (annotationId: string) => {
      if (editorRef.current) {
        removeAnnotationMarks(editorRef.current, annotationId);
      }
      onAnnotationsChange(
        annotationsRef.current.filter(a => a.id !== annotationId),
      );
    },
    [editorRef, onAnnotationsChange],
  );

  const handleCommentChange = useCallback(
    (annotationId: string, comment: string) => {
      onAnnotationsChange(
        annotationsRef.current.map(a =>
          a.id === annotationId ? { ...a, comment } : a,
        ),
      );
    },
    [onAnnotationsChange],
  );

  if (annotations.length === 0) {
    return (
      <Text size="caption" color="default">
        {formatMessage(intlMessages.emptyState)}
      </Text>
    );
  }

  return (
    <div className={classes.list}>
      {annotations.map(annotation => (
        <AnnotationCard
          key={annotation.id}
          annotation={annotation}
          onDelete={handleDelete}
          onCommentChange={handleCommentChange}
        />
      ))}
    </div>
  );
};
